import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search,
  Gift,
  CheckCircle,
  Circle,
  Star,
  Sparkles,
  Calendar,
  Loader2,
  Copy,
  Users,
  Heart,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PunchSlot = ({ filled, index, isReward }) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ delay: index * 0.08 }}
    className={`aspect-square flex items-center justify-center border-2 transition-all ${
      isReward
        ? "border-[#C5A059] bg-[#C5A059]/20"
        : filled
        ? "border-[#C5A059] bg-[#C5A059]"
        : "border-[#333] bg-[#1a1a1a]"
    }`}
  >
    {isReward ? (
      <Gift size={20} className="text-[#C5A059]" />
    ) : filled ? (
      <CheckCircle size={20} className="text-[#050505]" />
    ) : (
      <span className="font-heading text-sm text-[#333]">{index + 1}</span>
    )}
  </motion.div>
);

export const LoyaltyCardPage = () => {
  const [lookupValue, setLookupValue] = useState("");
  const [lookupType, setLookupType] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupValue.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const param = lookupType === "phone" ? `phone=${encodeURIComponent(lookupValue.trim())}` : `email=${encodeURIComponent(lookupValue.trim())}`;
      const res = await axios.get(`${API}/loyalty/lookup?${param}`);
      setCard(res.data);
    } catch {
      setCard({ found: false });
      toast.error("Could not look up loyalty card");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="loyalty-card-page" className="pt-20">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <Star className="w-10 h-10 text-[#C5A059] mx-auto mb-4" />
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Loyalty Rewards
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Punch<br />
              <span className="gold-gradient">Card</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] max-w-2xl mx-auto">
              Book 9 services and get your 10th service FREE! Every booking automatically
              earns you a punch. Check your progress below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-10 bg-[#0A0A0A] border-b border-[#262626]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 text-center">
            <div>
              <div className="w-12 h-12 mx-auto border border-[#C5A059] flex items-center justify-center mb-3">
                <Calendar size={20} className="text-[#C5A059]" />
              </div>
              <h3 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-1">Book</h3>
              <p className="font-body text-xs text-[#A3A3A3]">Any service counts as 1 punch</p>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto border border-[#C5A059] flex items-center justify-center mb-3">
                <Sparkles size={20} className="text-[#C5A059]" />
              </div>
              <h3 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-1">Earn</h3>
              <p className="font-body text-xs text-[#A3A3A3]">Collect 9 punches automatically</p>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto border border-[#C5A059] flex items-center justify-center mb-3">
                <Gift size={20} className="text-[#C5A059]" />
              </div>
              <h3 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-1">Free</h3>
              <p className="font-body text-xs text-[#A3A3A3]">10th service is on us!</p>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto border border-[#C5A059] flex items-center justify-center mb-3">
                <Users size={20} className="text-[#C5A059]" />
              </div>
              <h3 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-1">Refer</h3>
              <p className="font-body text-xs text-[#A3A3A3]">Both you & your friend earn a bonus punch!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lookup */}
      <section className="py-12 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-lg mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 className="font-heading text-xl text-white mb-2">Check Your Card</h2>
            <p className="font-body text-sm text-[#A3A3A3]">Enter the phone number or email you use when booking.</p>
          </div>

          <form onSubmit={handleLookup} className="space-y-4">
            <div className="flex gap-2">
              <button type="button" onClick={() => setLookupType("phone")}
                data-testid="lookup-by-phone"
                className={`flex-1 py-3 text-xs tracking-wider uppercase font-semibold transition-all ${
                  lookupType === "phone" ? "bg-[#C5A059] text-[#050505]" : "bento-card text-[#A3A3A3]"
                }`}>Phone</button>
              <button type="button" onClick={() => setLookupType("email")}
                data-testid="lookup-by-email"
                className={`flex-1 py-3 text-xs tracking-wider uppercase font-semibold transition-all ${
                  lookupType === "email" ? "bg-[#C5A059] text-[#050505]" : "bento-card text-[#A3A3A3]"
                }`}>Email</button>
            </div>

            <div className="flex gap-2">
              <input
                type={lookupType === "email" ? "email" : "tel"}
                value={lookupValue}
                onChange={(e) => setLookupValue(e.target.value)}
                placeholder={lookupType === "phone" ? "Enter your phone number" : "Enter your email address"}
                data-testid="loyalty-lookup-input"
                className="flex-1 luxury-input"
                required
              />
              <button type="submit" disabled={loading}
                data-testid="loyalty-lookup-btn"
                className="bg-[#C5A059] text-[#050505] px-6 py-3 font-semibold text-xs tracking-wider uppercase hover:brightness-110 transition-all disabled:opacity-40 flex items-center gap-2">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      {searched && card && (
        <section className="py-8 md:py-12 bg-[#050505]">
          <div className="max-w-lg mx-auto px-6 md:px-12">
            {!card.found ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="bento-card p-8 text-center" data-testid="loyalty-not-found">
                <Circle className="w-12 h-12 text-[#333] mx-auto mb-4" />
                <h3 className="font-heading text-lg text-white mb-2">No Card Found</h3>
                <p className="font-body text-sm text-[#A3A3A3] mb-6">
                  {card.message || "Book your first service to start earning punches!"}
                </p>
                <Link to="/book"
                  className="inline-block bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase px-8 py-3 hover:brightness-110 transition-all">
                  Book a Service
                </Link>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="loyalty-card-result">
                {/* Card Header */}
                <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#C5A059]/30 p-6 mb-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-[#C5A059]/5 rounded-full -mr-20 -mt-20" />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="font-body text-xs text-[#C5A059] uppercase tracking-wider">Luxury Beauty Loyalty</p>
                        <h3 className="font-heading text-xl text-white">{card.name || "Valued Client"}</h3>
                      </div>
                      <Star className="w-8 h-8 text-[#C5A059]" />
                    </div>
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-[#A3A3A3] block text-xs">Total Visits</span>
                        <span className="font-heading text-2xl text-white">{card.total_punches}</span>
                      </div>
                      <div>
                        <span className="text-[#A3A3A3] block text-xs">Free Services Earned</span>
                        <span className="font-heading text-2xl text-[#C5A059]">{card.rewards_earned}</span>
                      </div>
                      {card.rewards_available > 0 && (
                        <div>
                          <span className="text-[#A3A3A3] block text-xs">Ready to Redeem</span>
                          <span className="font-heading text-2xl text-emerald-400">{card.rewards_available}</span>
                        </div>
                      )}
                      {card.referrals_made > 0 && (
                        <div>
                          <span className="text-[#A3A3A3] block text-xs">Referrals</span>
                          <span className="font-heading text-2xl text-purple-400">{card.referrals_made}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Referral Code Section */}
                {card.referral_code && (
                  <div className="bento-card p-6 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Users size={18} className="text-[#C5A059]" />
                      <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                        Your Referral Code
                      </h4>
                    </div>
                    <div className="bg-[#0a0a0a] border border-[#C5A059]/20 p-4 flex items-center justify-between">
                      <div>
                        <span className="font-heading text-2xl text-[#C5A059]" data-testid="referral-code">{card.referral_code}</span>
                        <p className="font-body text-xs text-[#A3A3A3] mt-1">Share this code with friends — you both earn a bonus punch!</p>
                      </div>
                      <button
                        onClick={() => { navigator.clipboard.writeText(card.referral_code); toast.success("Referral code copied!"); }}
                        data-testid="copy-referral-code"
                        className="text-[#C5A059] hover:text-white transition-colors p-2"
                      >
                        <Copy size={20} />
                      </button>
                    </div>
                    {card.referrals_made > 0 && (
                      <p className="text-xs text-[#A3A3A3] mt-3">
                        You've referred <strong className="text-white">{card.referrals_made}</strong> friend{card.referrals_made !== 1 ? "s" : ""} and earned <strong className="text-[#C5A059]">{card.bonus_punches || 0}</strong> bonus punch{(card.bonus_punches || 0) !== 1 ? "es" : ""}!
                      </p>
                    )}
                  </div>
                )}

                {/* Punch Card Visual */}
                <div className="bento-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                      Current Progress
                    </h4>
                    <span className="text-xs text-[#A3A3A3]">
                      {card.current_cycle_punches}/9 punches
                    </span>
                  </div>

                  <div className="grid grid-cols-5 gap-2 mb-4">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <PunchSlot
                        key={i}
                        index={i}
                        filled={i < card.current_cycle_punches}
                        isReward={false}
                      />
                    ))}
                    <PunchSlot
                      index={9}
                      filled={false}
                      isReward={true}
                    />
                  </div>

                  <div className="text-center">
                    {card.rewards_available > 0 ? (
                      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 p-4">
                        <Gift className="w-6 h-6 text-[#C5A059] mx-auto mb-2" />
                        <p className="font-heading text-lg text-[#C5A059]">
                          You have {card.rewards_available} free service{card.rewards_available > 1 ? "s" : ""} to redeem!
                        </p>
                        <p className="font-body text-xs text-[#A3A3A3] mt-1">
                          Mention your loyalty card at your next visit for a free service (same type as your regular bookings).
                        </p>
                      </div>
                    ) : card.current_cycle_punches > 0 ? (
                      <p className="font-body text-sm text-[#A3A3A3]">
                        <span className="text-[#C5A059] font-semibold">{card.punches_until_free}</span> more visit{card.punches_until_free !== 1 ? "s" : ""} until your free service!
                      </p>
                    ) : (
                      <p className="font-body text-sm text-[#A3A3A3]">
                        Book your first service to start earning!
                      </p>
                    )}
                  </div>
                </div>

                {/* Recent Visits */}
                {card.recent_punches && card.recent_punches.length > 0 && (
                  <div className="bento-card p-6 mt-4">
                    <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                      Recent Visits
                    </h4>
                    <div className="space-y-3">
                      {card.recent_punches.slice().reverse().map((punch, i) => (
                        <div key={i} className="flex justify-between items-start text-sm border-b border-[#1a1a1a] pb-2 last:border-0">
                          <div>
                            <span className="text-white">{(punch.services || []).join(", ") || "Service"}</span>
                            <span className="block text-xs text-[#A3A3A3]">{punch.date || "—"}</span>
                          </div>
                          <CheckCircle size={14} className="text-[#C5A059] mt-1 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-center mt-6">
                  <Link to="/book"
                    className="inline-block bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase px-8 py-3 hover:brightness-110 transition-all">
                    Book Your Next Visit
                  </Link>
                </div>
              </motion.div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default LoyaltyCardPage;
